import { ClerkProvider, useAuth } from "@clerk/clerk-expo";
import { Stack } from "expo-router";
import { StatusBar } from "expo-status-bar";

import { resolveApiBaseUrl } from "../src/api-host";
import { setApiBaseUrl, setTokenProvider } from "../src/api";
import { createTokenCache } from "../src/token-cache";
import { colour } from "../src/theme";

// Once, at startup. The API client is deliberately free of Expo imports, so
// something has to tell it where the server is -- see src/api-host.ts.
setApiBaseUrl(resolveApiBaseUrl());

const publishableKey = process.env["EXPO_PUBLIC_CLERK_PUBLISHABLE_KEY"];

/**
 * Hands the API client a way to fetch the current session token.
 *
 * A function rather than the token itself, because tokens are short-lived and
 * Clerk refreshes them: caching one here would work for about a minute and
 * then start returning 401s that look like being signed out.
 */
function AttachToken({ children }: { children: React.ReactNode }) {
  const { getToken } = useAuth();
  setTokenProvider(getToken);
  return <>{children}</>;
}

export default function RootLayout() {
  if (!publishableKey) {
    // Better a clear message at startup than a Clerk error deep in a screen.
    // The key is public -- it belongs in .env, not in a secret store.
    throw new Error(
      "EXPO_PUBLIC_CLERK_PUBLISHABLE_KEY is not set. Copy .env.example to .env.",
    );
  }

  return (
    <ClerkProvider publishableKey={publishableKey} tokenCache={createTokenCache()}>
      <AttachToken>
        <Stack
          screenOptions={{
            headerStyle: { backgroundColor: colour.ink },
            headerTintColor: colour.canvas,
            contentStyle: { backgroundColor: colour.canvas },
          }}
        >
          {/*
            The tab group brings its own chrome, so the stack header would be
            a second bar over the top of it. Game detail and sign-in still
            push over the tabs with a back button, which is what they want.
          */}
          <Stack.Screen name="(tabs)" options={{ headerShown: false }} />
          <Stack.Screen name="game/[slug]" options={{ title: "Game" }} />
          <Stack.Screen name="sign-in" options={{ title: "Sign in" }} />
        </Stack>
        <StatusBar style="light" />
      </AttachToken>
    </ClerkProvider>
  );
}
